/**
 * Runtime environment configuration.
 *
 * Fetches /api/config before the app boots so that environment-specific values
 * are not baked into the bundle at build time.
 *
 * Falls back to process.env for local development (when /api/config is unreachable).
 */

export interface RuntimeConfig {
	SOS_ENV: string;
	SOS_SECURE: string;
	SOS_DEBUG: string;
	SENTRY_DEBUG: string;
	PENDO_KEY: string;
	AH_URL: string;
	STRIPE_PUBLIC_KEY: string;
	BASE_URL: string;
	SMART_ID_DOMAIN: string;
	PING_ACTIVITY_CHECK_FREQUENCY: string;
	SMART_ID_AH_GROUP: string;
	SESSION_CHECK_INTERVAL: string;
	INACTIVITY_INTERVAL: string;
	WARNING_TIMEOUT: string;
	DEBOUNCE_INTERVAL: string;
}

const CONFIG_URL = '/api/config';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

let _config: RuntimeConfig | null = null;
let _configReady: Promise<void> | null = null;

/**
 * Returns the singleton promise for the in-flight or resolved loadRuntimeConfig call.
 * Await this in any module that needs config before acting (e.g. Sentry init).
 */
export function getRuntimeConfigReady(): Promise<void> {
	if (!_configReady) throw new Error('loadRuntimeConfig() has not been called yet');
	return _configReady;
}

async function fetchWithRetry(url: string, retries: number): Promise<RuntimeConfig> {
	let lastError: unknown;
	for (let attempt = 1; attempt <= retries; attempt++) {
		try {
			const res = await fetch(url, { credentials: 'same-origin' });
			if (!res.ok) throw new Error(`/api/config responded ${res.status}`);
			return (await res.json()) as RuntimeConfig;
		} catch (err) {
			lastError = err;
			if (attempt < retries) {
				await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
			}
		}
	}
	throw lastError;
}

/**
 * Load runtime config from /api/config. Must be awaited before app boots.
 * In local dev, if the endpoint is unreachable the values fall back to process.env.
 */
export async function loadRuntimeConfig(): Promise<void> {
	if (_configReady) return _configReady;
	_configReady = (async () => {
		try {
			_config = await fetchWithRetry(CONFIG_URL, MAX_RETRIES);
		} catch (err) {
			const isLocal = process.env.SOS_ENV === 'local';
			if (isLocal) {
				// Graceful local-dev fallback — build-time values are acceptable here
				_config = {
					SOS_ENV: process.env.SOS_ENV || '',
					SOS_SECURE: process.env.SOS_SECURE || '',
					SOS_DEBUG: process.env.SOS_DEBUG || '',
					SENTRY_DEBUG: process.env.SENTRY_DEBUG || '',
					PENDO_KEY: process.env.PENDO_KEY || '',
					AH_URL: process.env.AH_URL || '',
					STRIPE_PUBLIC_KEY: process.env.STRIPE_PUBLIC_KEY || '',
					BASE_URL: process.env.BASE_URL || '',
					SMART_ID_DOMAIN: process.env.SMART_ID_DOMAIN || '',
					PING_ACTIVITY_CHECK_FREQUENCY: process.env.PING_ACTIVITY_CHECK_FREQUENCY || '',
					SMART_ID_AH_GROUP: process.env.SMART_ID_AH_GROUP || '',
					SESSION_CHECK_INTERVAL: process.env.SESSION_CHECK_INTERVAL || '',
					INACTIVITY_INTERVAL: process.env.INACTIVITY_INTERVAL || '',
					WARNING_TIMEOUT: process.env.WARNING_TIMEOUT || '',
					DEBOUNCE_INTERVAL: process.env.DEBOUNCE_INTERVAL || '',
				};
			} else {
				// Non-local: surface the error so root.ts can render a failure UI
				throw err;
			}
		}
	})();
	return _configReady;
}

/** Typed accessor — throws if called before loadRuntimeConfig() resolves. */
export function getRuntimeConfig(): RuntimeConfig {
	return _config!;
}

/** Convenience getter — returns '' if config is not loaded yet.
 *  In development, logs a warning so pre-boot calls are surfaced immediately. */
export function getConfigValue<K extends keyof RuntimeConfig>(key: K): string {
	if (!_config) {
		if (process.env.NODE_ENV !== 'production') {
			console.warn(
				`[runtimeEnv] getConfigValue('${key}') called before loadRuntimeConfig() resolved. ` +
				`Returning ''. Check the module that imported this at evaluation time.`
			);
		}
		return '';
	}
	return _config[key] ?? '';
}
